import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';

import { AssetClassesService } from 'src/app/services/asset-classes.service';
import { AssetClass, AssetClassSummary } from '@models/asset-class.interface';
import { AssetClassListComponent } from './asset-class-list/asset-class-list.component';
import { AssetClassDetailComponent, AssetClassSavedEvent } from './asset-class-detail/asset-class-detail.component';
import { ConfirmationDialogComponent, ConfirmationDialogData } from '@shared/components/confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-asset-classes-page',
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinnerModule,
    AssetClassListComponent,
    AssetClassDetailComponent
  ],
  templateUrl: './asset-classes-page.component.html',
  styleUrl: './asset-classes-page.component.scss'
})
export class AssetClassesPageComponent implements OnInit {
  private readonly assetClassesService = inject(AssetClassesService);
  private readonly dialog = inject(MatDialog);

  protected assetClasses = signal<AssetClassSummary[]>([]);
  protected selectedAssetClass = signal<AssetClass | null>(null);
  protected selectedId = signal<number | null>(null);
  protected newlyCreatedId = signal<number | null>(null);
  protected isLoadingList = signal(false);
  protected isLoadingDetail = signal(false);

  ngOnInit(): void {
    this.loadList();
  }

  private loadList(selectId?: number, forceRefresh = false): void {
    this.isLoadingList.set(true);
    this.assetClassesService.getList(forceRefresh).subscribe({
      next: list => {
        this.assetClasses.set(list);
        this.isLoadingList.set(false);

        const targetId = selectId ?? this.selectedId();
        if (targetId !== null) {
          const stillPresent = list.find(a => a.id === targetId);
          if (stillPresent) {
            this.loadDetail(targetId, forceRefresh && selectId === targetId);
          } else {
            this.selectedAssetClass.set(null);
            this.selectedId.set(null);
          }
        }
      },
      error: () => {
        this.isLoadingList.set(false);
      }
    });
  }

  private loadDetail(id: number, forceRefresh = false): void {
    this.isLoadingDetail.set(true);
    this.selectedId.set(id);
    this.assetClassesService.getById(id, forceRefresh).subscribe({
      next: assetClass => {
        this.selectedAssetClass.set(assetClass);
        this.isLoadingDetail.set(false);
      },
      error: () => {
        this.selectedAssetClass.set(null);
        this.isLoadingDetail.set(false);
      }
    });
  }

  protected onAssetClassSelected(summary: AssetClassSummary): void {
    this.newlyCreatedId.set(null);
    this.loadDetail(summary.id);
  }

  protected onAddClicked(): void {
    this.newlyCreatedId.set(null);
    const newAssetClass: AssetClass = {
      id: 0,
      name: '',
      alias: null,
      createdOn: new Date().toISOString()
    };
    this.selectedAssetClass.set(newAssetClass);
    this.selectedId.set(null);
  }

  protected onAssetClassSaved(event: AssetClassSavedEvent): void {
    this.selectedId.set(event.id);
    this.newlyCreatedId.set(event.id);
    this.loadList(event.id, true);
  }

  protected onAssetClassDeleted(assetClassId: number): void {
    const dialogRef = this.dialog.open<ConfirmationDialogComponent, ConfirmationDialogData, boolean>(
      ConfirmationDialogComponent,
      {
        data: {
          title: 'Delete Asset Class',
          primaryText: 'Are you sure you want to delete this asset class? Business units using it will be cleared.',
          confirmText: 'Delete',
        },
        width: '420px',
        disableClose: true,
      }
    );
    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.assetClassesService.delete(assetClassId).subscribe({
        next: () => {
          if (this.selectedId() === assetClassId) {
            this.selectedAssetClass.set(null);
            this.selectedId.set(null);
          }
          this.loadList();
        },
        error: () => {
          alert('Failed to delete asset class. Please try again.');
        }
      });
    });
  }
}